import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { ImagePreviewDialogComponent } from '@app/shared/image-preview-dialog/image-preview-dialog.component';
import { env } from '../../../environments/env';

@Component({
  selector: 'app-secured-resource',
  templateUrl: './secured-resource.component.html',
  styleUrls: ['./secured-resource.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SecuredResourceComponent implements OnChanges {
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
  ) {}

  @Input() workOrderId!: string;
  @Input() commentId!: string;
  @Input() contentType!: string;
  @Input() attachmentUrl!: string;

  dataUrl: any = null;
  loading = true;
  imageReady = false;

  @Input() disabled = false;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.disabled) {
      // Local preview active → do NOT fetch secured resource yet
      return;
    }

    if (changes['attachmentUrl'] && this.attachmentUrl) {
      await this.loadWithRetry();
    }
  }

  isImage() {
    return this.contentType?.startsWith('image');
  }

  isVideo() {
    return this.contentType?.startsWith('video');
  }

  openPreview(): void {
    if (!this.isImage() || !this.dataUrl) {
      return;
    }

    this.dialog.open(ImagePreviewDialogComponent, {
      data: { url: this.dataUrl },
      panelClass: 'image-preview-dialog',
      backdropClass: 'image-preview-dialog-backdrop',
      maxWidth: '95vw',
      maxHeight: '95vh',
      width: '95vw',
      height: '95vh',
      autoFocus: false,
    });
  }

  private async loadWithRetry() {
    this.loading = true;
    this.imageReady = false;

    const url = `${env.get().api.url}/work-orders/${this.workOrderId}/comments/${
      this.commentId
    }/attachment?ts=${Date.now()}`;

    for (let attempt = 0; attempt < 15; attempt++) {
      try {
        const blob = await this.http.get(url, { responseType: 'blob' }).toPromise();

        if (blob && blob.size > 0) {
          const objectUrl = URL.createObjectURL(blob);
          this.dataUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
          this.loading = false;
          return;
        }
      } catch (err) {
        if (err.status !== 404) {
          console.error('Attachment error:', err);
          break;
        }
        // 404 is expected until backend finishes changelog
      }

      // wait 200–300ms
      await new Promise((r) => setTimeout(r, 300));
    }

    console.warn('Attachment still not available after polling.');
    this.loading = false;
  }
}
